import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Heart, MapPin, Users, Compass } from 'lucide-react';

const AboutPage = () => {
  const { i18n } = useTranslation();
  
  return (
    <main className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="relative h-[40vh] overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1469474968028-56623f02e42e?w=1920&q=80"
          alt="Nature landscape"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 to-black/70 flex items-center justify-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center text-white"
          >
            <h1 className="text-4xl md:text-5xl font-bold font-serif mb-4">À propos</h1>
            <p className="text-xl text-white/80">L'histoire de XPerience</p>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 px-4">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="bg-white rounded-2xl shadow-lg p-8 md:p-12"
          >
            {/* Logo */}
            <div className="text-center mb-8">
              <span className="text-4xl font-bold text-primary font-serif">XPerience</span>
              <p className="text-gray-500 mt-2 italic">"Voyagez autrement, rencontrez vraiment."</p>
            </div>

            {/* Story */}
            <div className="prose prose-lg max-w-none text-gray-700 space-y-6">
              <p>
                <strong className="text-primary">XPerience</strong> voit le jour en 2026, grâce à Emergent. L'idée est restée dans un tiroir durant de longues années, faute de temps, d'engagement et de moyens. Mais voilà, elle n'a jamais vraiment été oubliée.
              </p>

              <p>
                Alors, en voici un peu le concept : tu vis dans une région, peu importe laquelle, et tu as une ou des choses à faire découvrir moyennant un tarif. C'est <strong>là</strong> que la plateforme intervient afin de mettre en relation guides et explorateurs.
              </p>

              <p className="text-lg font-medium text-gray-800 bg-primary/5 p-4 rounded-lg border-l-4 border-primary">
                Que ce soit pour un week-end à Lisbonne, une randonnée au Cervin ou peut-être du surf à Tarifa, tu es au bon endroit !
              </p>

              <p>
                Tu souhaites aller à l'essentiel, savoir où tu arrives, ne pas trop perdre de temps ? Ici, tu as la solution ! Mets-toi en relation avec un guide et kiffe.
              </p>

              <p>
                Pas de carte, pas de prise de tête et pas de perte de temps. Tu te mets en relation avec un guide local et tu <strong className="text-primary">Xplore</strong> !
              </p>

              <p>
                De plus, ton guide a peut-être un véhicule, et/ou un logement, et pourquoi pas un bateau, voire un hélico !
              </p>

              <p className="italic text-gray-600">
                Un job de rêve ? Peut-être, un peu, beaucoup et on l'espère, passionnément !
              </p>

              <p className="text-lg">
                Je souhaite que cette plateforme réunisse l'humain et lui permette de vivre des <strong className="text-primary">Xpériences</strong> hors du commun.
              </p>

              {/* Signature */}
              <div className="pt-8 border-t border-gray-200 mt-8">
                <p className="text-gray-600 mb-2">Bien à vous,</p>
                <p className="text-2xl font-handwritten text-primary font-bold">L'équipe XPerience</p>
              </div>
            </div>
          </motion.div>

          {/* Values Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-6"
          >
            {[
              { icon: Users, label: 'Connexion humaine', color: 'bg-orange-100 text-orange-600' },
              { icon: Compass, label: 'Aventure', color: 'bg-blue-100 text-blue-600' },
              { icon: MapPin, label: 'Authenticité', color: 'bg-green-100 text-green-600' },
              { icon: Heart, label: 'Passion', color: 'bg-red-100 text-red-600' }
            ].map((value, index) => (
              <div
                key={index}
                className="bg-white rounded-xl p-6 shadow-md text-center hover:shadow-lg transition-shadow"
              >
                <div className={`w-12 h-12 ${value.color} rounded-full flex items-center justify-center mx-auto mb-3`}>
                  <value.icon className="w-6 h-6" />
                </div>
                <p className="font-medium text-gray-800">{value.label}</p>
              </div>
            ))}
          </motion.div>
        </div>
      </section>
    </main>
  );
};

export default AboutPage;
